import { apiGet, decodeClaims, getToken } from '@/lib/api';

/**
 * The two aggregate dashboard calls, with a short-lived promise cache so the
 * cards, charts and tables that share a dashboard all read one response
 * instead of each fetching their own (the KPI grid, wallet cards, trend and
 * status charts of the admin dashboard mount together).
 */

export interface CustomerKpis {
  totalSolutions: number;
  totalContributed: number;
  activeProjects: number;
  pendingQuestions: number;
  milestonesCompleted: number;
  milestonesTotal: number;
}

export interface MonthlyPoint {
  monthStart: string;
  monthLabel: string;
  contributed: number;
}

export interface StatusCount {
  status: string;
  count: number;
}

export interface CustomerDashboard {
  kpis: CustomerKpis;
  trend: MonthlyPoint[];
  solutionsByStatus: StatusCount[];
}

export interface AdminKpis {
  newSolutionRequests: number;
  unallocatedContributions: number;
  unallocatedCount: number;
  contributionsToday: number;
  paymentsTodayCount: number;
  totalContributions: number;
  totalSolutions: number;
  projectsInProgress: number;
  projectsInTesting: number;
  projectsCompleted: number;
  awaitingCustomerReview: number;
}

export interface WalletRow {
  resourceWalletId: number;
  provider: string;
  resourceType: string;
  currentBalance: number;
  reservedBalance: number;
  availableBalance: number;
  unit: string;
  currency: string;
  lastUpdated: string;
}

export interface WeeklyPoint {
  weekStart: string;
  weekLabel: string;
  contributions: number;
  allocated: number;
}

export interface ProjectStats {
  inProgress: number;
  testing: number;
  atRisk: number;
  completed: number;
  awaitingReview: number;
}

export interface AdminDashboard {
  kpis: AdminKpis;
  wallets: WalletRow[];
  trend: WeeklyPoint[];
  solutionsByStatus: StatusCount[];
  projectStats: ProjectStats;
}

const TTL_MS = 15_000;

interface CacheEntry<T> {
  key: string;
  at: number;
  promise: Promise<T>;
}

let customerCache: CacheEntry<CustomerDashboard> | null = null;
let adminCache: CacheEntry<AdminDashboard> | null = null;

function currentUserKey(): string {
  return decodeClaims(getToken())?.userId ?? '';
}

export function getCustomerDashboard(): Promise<CustomerDashboard> {
  const key = currentUserKey();
  if (customerCache && customerCache.key === key && Date.now() - customerCache.at < TTL_MS)
    return customerCache.promise;
  const promise = apiGet<CustomerDashboard>('/api/dashboard/customer').catch((err) => {
    customerCache = null;
    throw err;
  });
  customerCache = { key, at: Date.now(), promise };
  return promise;
}

export function getAdminDashboard(): Promise<AdminDashboard> {
  const key = currentUserKey();
  if (adminCache && adminCache.key === key && Date.now() - adminCache.at < TTL_MS)
    return adminCache.promise;
  const promise = apiGet<AdminDashboard>('/api/dashboard/admin').catch((err) => {
    adminCache = null;
    throw err;
  });
  adminCache = { key, at: Date.now(), promise };
  return promise;
}

/** After a write that changes the numbers (an allocation, a payment) or a sign-in/out. */
export function invalidateDashboards(): void {
  customerCache = null;
  adminCache = null;
}
